import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import { FaPlus } from 'react-icons/fa';
import { MdModeEdit } from 'react-icons/md';
import { GrRevert } from 'react-icons/gr';

import { useAppContext } from '../contextApi/context';
import { getReceiptMovements, getReceiptMovementsTotalValue } from '../services/StockMovementService';
import { exportReceipts } from '../services/ExportService';
import StockMovementDetailsModal from '../components/shared/StockMovementDetailsModal';
import TableContainer from '../components/table/TableContainer';
import MovementAnnulmentModal from '../components/MovementAnnulmentModal';
import { TableActionButton, AddButton } from '../styles/StyledComponents';
import { formatDateAndTime, formatDate } from '../utils/dates';
import { formatCurrency } from '../utils/numbers';
import { checkPermission } from '../utils/permissions';
import EditMovementAnnulmentModal from '../components/EditMovementAnnulmentModal';

const Receipts = () => {
    // ----- Context/Hooks
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { loggedUser } = useAppContext();

    // ----- State
    let [movementId, setMovementId] = useState(null);
    let [showDetailsModal, setShowDetailsModal] = useState(false);
    let [showMovementCreated, setShowMovementCreated] = useState(false);
    let [showAnnulmentModal, setShowAnnulmentModal] = useState(false);
    let [showEditAnnulmentModal, setShowEditAnnulmentModal] = useState(false);
    let [showMovementEdited, setShowMovementEdited] = useState(false);
    let [tableTotalValue, setTableTotalValue] = useState(null);
    const [updateTable, setUpdateTable] = useState(false);

    // ----- API Fetch Actions
    const fetchTotalValueData = useCallback(
        async (filters = []) => {
            return await getReceiptMovementsTotalValue(loggedUser.token, filters);
        },
        [loggedUser.token]
    );

    // ----- Hooks
    useEffect(() => {
        fetchTotalValueData().then((data) => {
            setTableTotalValue(data);
        });

        if (searchParams.get('created')) {
            setShowMovementCreated(true);
        }
    }, [searchParams, fetchTotalValueData]);

    // ----- Actions
    const handleShowDetails = (id) => {
        setMovementId(id);
        setShowDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setMovementId(null);
        setShowDetailsModal(false);
    };

    const handleDataAfterFiltering = (filters) => {
        fetchTotalValueData(filters).then((data) => {
            setTableTotalValue(data);
        });
    };

    const handleShowAnnulmentModal = (id) => {
        setMovementId(id);
        setShowAnnulmentModal(true);
    };

    const handleCloseAnnulmentModal = () => {
        setMovementId(null);
        setShowAnnulmentModal(false);
    };

    const handleAnnulment = () => {
        setShowMovementCreated(false);
        setShowAnnulmentModal(false);
        setUpdateTable(!updateTable);
    };

    const handleShowEditReturnModal = (id) => {
        setMovementId(id);
        setShowEditAnnulmentModal(true);
    };

    const handleCloseEditAnnulmentModal = () => {
        setMovementId(null);
        setShowEditAnnulmentModal(false);
    };

    const handleEditAnnulment = () => {
        setShowMovementEdited(true);
        setShowEditAnnulmentModal(false);
        setUpdateTable(!updateTable);
    };

    // ----- Constants
    const Actions = ({ row }) => (
        <div className='d-flex'>
            <TableActionButton className='btn btn-primary' onClick={() => handleShowDetails(row.id)}>
                <FaMagnifyingGlass />
                Detalles
            </TableActionButton>

            <OverlayTrigger
                delay={{ hide: 200, show: 300 }}
                overlay={(props) => <Tooltip {...props}>{row.returnMovement === null ? 'Hacer una anulación' : 'Editar la anulación'}</Tooltip>}
                placement='top'
            >
                <TableActionButton
                    className='btn btn-primary'
                    disabled={(row.returnMovement !== null && row.annulmentType) === 'COMPLETE'}
                    onClick={
                        row.returnMovement === null ? () => handleShowAnnulmentModal(row.id) : () => handleShowEditReturnModal(row.returnMovement)
                    }
                >
                    {row.returnMovement === null ? <GrRevert /> : <MdModeEdit />}
                    Editar
                </TableActionButton>
            </OverlayTrigger>
        </div>
    );

    const columns = [
        {
            name: 'Fecha de registro',
            selector: (row) => formatDateAndTime(row.createdDate),
            sortable: true,
            sortField: 'createdDate',
        },
        {
            name: 'Factura',
            selector: (row) => row.invoice,
            sortable: true,
            sortField: 'invoice',
        },
        {
            name: 'Fecha de factura',
            selector: (row) => (row.eventDate ? formatDate(row.eventDate) : '--'),
            sortable: true,
            sortField: 'eventDate',
        },
        {
            name: 'Proveedor',
            selector: (row) => row.provider,
            sortable: true,
            sortField: 'provider',
        },
        {
            name: 'Valor total',
            selector: (row) => `₡${formatCurrency(row.totalValueWithReturn)}`,
            sortable: true,
            sortField: 'totalValueWithReturn',
        },
        {
            name: 'Acciones',
            width: '210px',
            cell: (row) => <Actions row={row} />,
        },
    ];

    const filters = [
        {
            column: 'createdDate',
            input: 'dates',
            label: 'Fecha de registro',
            operation: 'between',
        },
        {
            column: 'invoice',
            input: 'text',
            label: 'Factura',
            operation: 'like',
            length: 10,
        },
        {
            column: 'eventDate',
            input: 'dates',
            label: 'Fecha de factura',
            operation: 'between',
        },
        {
            column: 'annulmentType',
            input: 'select',
            label: 'Anulación',
            options: [
                { value: '--', text: 'Sin Anulación' },
                { value: 'PARTIAL', text: 'Anulación parcial' },
                { value: 'COMPLETE', text: 'Anulación completa' },
            ],
            operation: 'equal',
        },
        {
            column: 'provider',
            input: 'text',
            label: 'Proveedor',
            operation: 'like',
            length: 50,
        },
        {
            column: 'totalValueWithReturn',
            input: 'range',
            label: 'Valor total',
            operation: 'between',
            length: 15,
        },
        {
            column: 'article',
            input: 'text',
            label: 'Artículo',
            operation: 'like',
            length: 50,
        },
    ];

    const exportOptions = checkPermission(loggedUser.permissions, 'export_stock_receipt')
        ? {
              handler: exportReceipts,
          }
        : null;

    // ----- Render
    return (
        <div className='container mt-5'>
            <h1 className=''>Compras</h1>

            {showMovementCreated && (
                <div className='alert alert-success text-center mt-3' role='alert'>
                    Compra creada satisfactoriamente.
                </div>
            )}

            {showMovementEdited && (
                <div className='alert alert-success text-center mt-3' role='alert'>
                    Compra editada satisfactoriamente.
                </div>
            )}

            {checkPermission(loggedUser.permissions, 'manage_stock_receipt') && (
                <div className='d-flex mt-3 justify-content-end'>
                    <AddButton className='btn btn-primary' onClick={() => navigate('/crear-compra')}>
                        <FaPlus /> Nueva compra
                    </AddButton>
                </div>
            )}

            <TableContainer
                afterFilteringHandler={handleDataAfterFiltering}
                columns={columns}
                fetchDataHandler={getReceiptMovements}
                updateTable={updateTable}
                filters={filters}
                exportOptions={exportOptions}
                totalValue={tableTotalValue}
            />

            {showDetailsModal && <StockMovementDetailsModal movementId={movementId} closeHandler={handleCloseDetailsModal} show={showDetailsModal} />}

            {showAnnulmentModal && (
                <MovementAnnulmentModal
                    movementId={movementId}
                    closeHandler={handleCloseAnnulmentModal}
                    actionHandler={handleAnnulment}
                    show={showAnnulmentModal}
                />
            )}

            {showEditAnnulmentModal && (
                <EditMovementAnnulmentModal
                    annulmentId={movementId}
                    closeHandler={handleCloseEditAnnulmentModal}
                    actionHandler={handleEditAnnulment}
                    show={showEditAnnulmentModal}
                />
            )}
        </div>
    );
};

export default Receipts;
